import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import { dialogs, i18next, customization, view, hdrm } from '@yola/ws-sdk';
import DesignOtherStylesDialog from '../components/design-other-styles-dialog';
import DesignOtherStylesContext from '../helpers/design-other-styles-context';
import getOtherStylesSections from '../helpers/get-other-styles-sections';
import generateAdjustedEventTraits from '../helpers/generate-adjusted-event-traits';
import {
  SUBMIT_BUTTON_TRIGGER_ID,
  DIALOG_BACKGROUND_TRIGGER_ID,
} from '../../dialogs/constants/common.js';
import readOnlyModeDecorator from '../helpers/read-only-mode-decorator.js';

const { updateStrategies } = hdrm.constants;

const getCaptions = () => ({
  title: i18next.t('Customize other styles'),
  description: i18next.t(
    'Adjust additional style settings to fine-tune your website’s overall look'
  ),
  breadcrumbs: [i18next.t('Design'), i18next.t('Other styles')],
  searchPlaceholder: i18next.t('Search...'),
  emptyStateTitle: i18next.t('Sorry!'),
  emptyStateSubtitle: i18next.t('Nothing was found'),
  submitButton: i18next.t('Save'),
  cancelButton: i18next.t('Cancel'),
});

const DesignOtherStylesContainer = ({ onDialogMainAction, onDialogCancel, sourceId }) => {
  const initialState = useRef(customization.helpers.getCustomGlobalStylesVariables());
  const changedOptions = useRef([]);
  const [state, setState] = useState(initialState.current);

  const dispatch = useDispatch();

  const captions = getCaptions();
  const sections = getOtherStylesSections();

  const onOptionChange = (optionId, value, properties) => {
    changedOptions.current = [...changedOptions.current, optionId];

    const changedVariables = properties.reduce(
      (acc, property) => ({ ...acc, [property]: value }),
      {}
    );

    customization.helpers.setCustomGlobalStylesVariables(
      changedVariables,
      updateStrategies.UPDATE_LIVE_ONLY
    );
    setState((prevState) => ({ ...prevState, ...changedVariables }));
  };

  const handleSubmit = (event) => {
    customization.helpers.setCustomGlobalStylesVariables(
      state,
      updateStrategies.UPDATE_STATIC_ONLY
    );

    const adjustedTraits = generateAdjustedEventTraits(changedOptions.current);

    onDialogMainAction({
      sourceId,
      triggerId: event ? SUBMIT_BUTTON_TRIGGER_ID : DIALOG_BACKGROUND_TRIGGER_ID,
      ...adjustedTraits,
    });
    dialogs.operations.hide();
  };

  const handleCancel = () => {
    if (!isEqual(initialState.current, state)) {
      dispatch(view.actions.forceReloadView());
    }

    onDialogCancel({
      sourceId,
    });
    dialogs.operations.hide();
  };

  return (
    <DesignOtherStylesContext.Provider
      value={{ state, onOptionChange: readOnlyModeDecorator(onOptionChange) }}
    >
      <DesignOtherStylesDialog
        captions={captions}
        sections={sections}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </DesignOtherStylesContext.Provider>
  );
};

DesignOtherStylesContainer.propTypes = {
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
  sourceId: PropTypes.string.isRequired,
};

export default DesignOtherStylesContainer;
